import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import QueryString from 'qs';
import { ObjectUtil } from '@utils/ObjectUtil';

type Props<T> = {
  defaultFilter: T;
  whiteList: readonly string[];
};

/**
 * useSearchParamsFilter에서 필터 값 변경 시 디바운스 처리
 * @param filterFn 필터 값 변경 시 호출할 함수
 * @param value 필터 값
 * @returns
 */
export const debouncedChangeFilter = debounce((filterFn: (value: string) => void, value: string) => {
  filterFn(value);
}, 500);

/**
 * URL Query String과 조회 API의 Paramter를 연동하기 위한 custom hook.
 * @param defaultFilter - API Paramter 기본값
 * @param whiteList - API Parameter 중 URL에서 표시할 Parameter 목록
 */
export const useSearchParamsFilter = <T extends Record<string, unknown>>({ defaultFilter, whiteList }: Props<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const whiteListRef = useRef(whiteList);

  const [filter, setFilter] = useState<T>(() => {
    const params: Record<string, string | string[]> = {};
    whiteListRef.current.forEach(key => {
      const value = searchParams.getAll(key);
      if (value.length > 0) {
        if (value.length === 1) {
          params[key] = value[0];
        } else {
          params[key] = value;
        }
      }
    });
    return { ...defaultFilter, ...params };
  });

  // URL 동기화를 위한 ref (useEffect에서 사용)
  const shouldSyncUrlRef = useRef(false);
  const defaultFilterRef = useRef(defaultFilter);

  // filter 상태가 변경되면 URL 동기화
  useEffect(() => {
    if (!shouldSyncUrlRef.current) {
      return;
    }

    const urlQueryObject: { [key in (typeof whiteListRef.current)[number]]?: unknown } = {};
    whiteListRef.current.forEach(param => {
      if (filter[param] !== undefined && filter[param] !== '') {
        urlQueryObject[param] = filter[param];
      }
    });
    const queryString = QueryString.stringify(urlQueryObject, { arrayFormat: 'repeat', skipNulls: true });
    setSearchParams(queryString, { replace: true });
  }, [filter, setSearchParams]);

  // updateFilter: useCallback으로 참조 안정성 확보, setFilter 함수형 업데이트로 stale closure 방지
  const updateFilter = useCallback((newFilter: React.SetStateAction<T>) => {
    setFilter(prevFilter => {
      const newFilterObject = typeof newFilter === 'function' ? newFilter(prevFilter) : newFilter;
      const removeEmpty = ObjectUtil.removeEmpty(newFilterObject);

      if (Object.keys(removeEmpty).length === 0) {
        shouldSyncUrlRef.current = true;
        return defaultFilterRef.current;
      }

      shouldSyncUrlRef.current = true;
      return removeEmpty;
    });
  }, []);

  const clearFilter = useCallback(() => {
    shouldSyncUrlRef.current = true;
    setFilter(defaultFilterRef.current);
  }, []);

  return [filter, updateFilter, clearFilter] as const;
};
